/**
 * Kanban Settings Store
 *
 * Manages Kanban view settings including card field visibility, column sorting,
 * card size preferences, and empty column visibility.
 * Persists user preferences to localStorage using Zustand persist middleware.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { createCloudSync } from '@shared/store/zustandCloudSync';

/**
 * Card field visibility configuration
 */
interface VisibleFields {
  owner: boolean;
  dueDate: boolean;
  orgCount: boolean;
  progressPercent: boolean;
  campaignType: boolean;
  createdDate: boolean;
  labels: boolean;
  tags: boolean;
}

/**
 * Column sort configuration
 */
interface ColumnSort {
  field: string;
  direction: 'asc' | 'desc';
}

/**
 * Card size options
 */
type CardSize = 'compact' | 'normal' | 'expanded';

/**
 * Default field visibility - all fields visible by default
 */
const DEFAULT_VISIBLE_FIELDS: VisibleFields = {
  owner: true,
  dueDate: true,
  orgCount: true,
  progressPercent: true,
  campaignType: true,
  createdDate: true,
  labels: true,
  tags: true,
};

/**
 * Kanban store state interface
 */
interface KanbanState {
  // Card field visibility
  visibleFields: VisibleFields;

  // Column settings (keyed by column ID/status)
  columnSort: Record<string, ColumnSort>;

  // View preferences
  cardSize: CardSize;
  showEmptyColumns: boolean;

  // Field visibility actions
  toggleField: (field: keyof VisibleFields) => void;
  showField: (field: keyof VisibleFields) => void;
  hideField: (field: keyof VisibleFields) => void;
  showAllFields: () => void;
  hideAllFields: () => void;

  // Column sort actions
  setColumnSort: (columnId: string, field: string, direction: 'asc' | 'desc') => void;
  clearColumnSort: (columnId: string) => void;
  clearAllColumnSorts: () => void;

  // View preference actions
  setCardSize: (size: CardSize) => void;
  toggleEmptyColumns: () => void;

  // Utility actions
  resetToDefaults: () => void;
  getVisibleFieldCount: () => number;
  getHiddenFieldCount: () => number;
}

/**
 * Kanban Settings Store
 *
 * Persists to localStorage with key 'kanban-view-settings'
 */
export const useKanbanStore = create<KanbanState>()(
  persist(
    (set, get) => ({
      // Initial state
      visibleFields: DEFAULT_VISIBLE_FIELDS,
      columnSort: {},
      cardSize: 'normal',
      showEmptyColumns: true,

      // Toggle field visibility
      toggleField: (field) => {
        set((state) => ({
          visibleFields: {
            ...state.visibleFields,
            [field]: !state.visibleFields[field],
          },
        }));
      },

      // Show a specific field
      showField: (field) => {
        set((state) => ({
          visibleFields: {
            ...state.visibleFields,
            [field]: true,
          },
        }));
      },

      // Hide a specific field
      hideField: (field) => {
        set((state) => ({
          visibleFields: {
            ...state.visibleFields,
            [field]: false,
          },
        }));
      },

      // Show all fields
      showAllFields: () => {
        set({
          visibleFields: DEFAULT_VISIBLE_FIELDS,
        });
      },

      // Hide all fields
      hideAllFields: () => {
        set({
          visibleFields: {
            owner: false,
            dueDate: false,
            orgCount: false,
            progressPercent: false,
            campaignType: false,
            createdDate: false,
            labels: false,
            tags: false,
          },
        });
      },

      // Set sort configuration for a specific column
      setColumnSort: (columnId, field, direction) => {
        set((state) => ({
          columnSort: {
            ...state.columnSort,
            [columnId]: { field, direction },
          },
        }));
      },

      // Clear sort configuration for a specific column
      clearColumnSort: (columnId) => {
        set((state) => {
          const newColumnSort = { ...state.columnSort };
          delete newColumnSort[columnId];
          return { columnSort: newColumnSort };
        });
      },

      // Clear all column sort configurations
      clearAllColumnSorts: () => {
        set({ columnSort: {} });
      },

      // Set card size preference
      setCardSize: (size) => {
        set({ cardSize: size });
      },

      // Toggle empty columns visibility
      toggleEmptyColumns: () => {
        set((state) => ({
          showEmptyColumns: !state.showEmptyColumns,
        }));
      },

      // Reset all settings to defaults
      resetToDefaults: () => {
        set({
          visibleFields: DEFAULT_VISIBLE_FIELDS,
          columnSort: {},
          cardSize: 'normal',
          showEmptyColumns: true,
        });
      },

      // Get count of visible fields
      getVisibleFieldCount: () => {
        const { visibleFields } = get();
        return Object.values(visibleFields).filter(Boolean).length;
      },

      // Get count of hidden fields
      getHiddenFieldCount: () => {
        const { visibleFields } = get();
        return Object.values(visibleFields).filter((v) => !v).length;
      },
    }),
    {
      name: 'kanban-view-settings', // localStorage key
      partialize: (state) => ({
        visibleFields: state.visibleFields,
        columnSort: state.columnSort,
        cardSize: state.cardSize,
        showEmptyColumns: state.showEmptyColumns,
      }),
    }
  )
);

// ==================== Cloud Sync ====================

// Cloud sync configuration
const kanbanCloudSync = createCloudSync<{
  visibleFields: VisibleFields;
  columnSort: Record<string, ColumnSort>;
  cardSize: CardSize;
  showEmptyColumns: boolean;
}>({
  key: 'kanban-view-settings',
  namespace: 'ui-preferences',
  debounceMs: 1000,
});

// Subscribe to state changes for auto-sync
useKanbanStore.subscribe((state) => {
  kanbanCloudSync.syncToCloud({
    visibleFields: state.visibleFields,
    columnSort: state.columnSort,
    cardSize: state.cardSize,
    showEmptyColumns: state.showEmptyColumns,
  });
});

/**
 * Load from cloud on app initialization
 * Call this during app startup to restore preferences from PostgreSQL
 */
export async function initKanbanFromCloud(): Promise<void> {
  const cloudState = await kanbanCloudSync.loadFromCloud();
  if (cloudState) {
    useKanbanStore.setState(cloudState);
  }
}
